import MaterialThumb from './MaterialThumb';
import InfoIcon from '@mui/icons-material/Info';
import { Box, Card, CardContent, CardHeader, Typography } from '@mui/material';
import { ReactNode } from 'react';
import { useRecoilValue } from 'recoil';
import HtmlTooltip from 'tooltip/HtmlTooltip';
import { BoardType, Material } from 'Types';

import BoardBase from 'boardClasses/BoardBase';
import { settingsAtom } from 'state/recoilState';
import {
  detailsToPrice,
  dimensionsToFBM,
  displayNumber,
  displayPercent,
  displayPrice,
  getDisplayLengthInUnits,
  getMaterialName,
  getSettingsInUnits,
} from 'utils/utils';

function EditorDetailsCard<T extends BoardType>(props: {
  boardInstance: BoardBase<T>;
}) {
  const { boardInstance } = props;
  const settings = useRecoilValue(settingsAtom);

  const { units } = boardInstance;

  const details = boardInstance.getBoardDetails(
    getSettingsInUnits(settings, units)
  );

  const displayLength = getDisplayLengthInUnits(units);

  if (details == null) {
    return (
      <Card>
        <CardHeader title="Details" />
        <CardContent>No details available</CardContent>
      </Card>
    );
  }

  const rawPrice = detailsToPrice(details, settings, units);

  return (
    <Card>
      <CardHeader
        title={
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            Details
            <HtmlTooltip
              placement="right"
              title={
                <Card>
                  <CardHeader title="Cost Esimates"></CardHeader>
                  <CardContent>
                    <Typography>
                      Cost estimates are based off of how I make each of these
                      boards. Your process and actual cost may vary. Cost
                      estimates are based off of material price, kerf, plane
                      buffer, and other expected waste. You can change these
                      values in the settings.
                    </Typography>
                  </CardContent>
                </Card>
              }
            >
              <InfoIcon sx={{ height: 24 }}></InfoIcon>
            </HtmlTooltip>
          </Box>
        }
        subheader={
          <span>
            Cost Estimate: {displayPrice(rawPrice)} +{' '}
            {displayPercent(settings.waste)} waste ={' '}
            <b>{displayPrice(rawPrice * (1 + settings.waste))}</b>
          </span>
        }
      />
      <CardContent>
        {details.idealStartingStock.map(({ label, stock }, index) => {
          const rows = Object.values(stock).map(
            ({ dimensions, material }, index) => {
              if (dimensions == null) {
                return null;
              }
              const { length, width, thickness } = dimensions;
              const fbm = dimensionsToFBM(dimensions, units);
              const materialCost = material
                ? settings.materialCosts[material]
                : 0;

              return (
                <MaterialLine
                  isFirst={+index === 0}
                  key={index}
                  material={material}
                >
                  {getMaterialName(material)}: {displayLength(length)} x{' '}
                  {displayLength(width)} x {displayLength(thickness)} (
                  {displayNumber(fbm)}fbm ={' '}
                  {displayPrice(materialCost ? fbm * materialCost.price : 0)})
                </MaterialLine>
              );
            }
          );

          return (
            <Box key={index} sx={{ pt: +index === 0 ? 0 : 2 }}>
              {label && <Typography variant="subtitle2">{label}</Typography>}
              {rows}
            </Box>
          );
        })}
      </CardContent>
    </Card>
  );
}

function MaterialLine(props: {
  children: ReactNode;
  isFirst: boolean;
  material: Material | null;
}) {
  const { children, isFirst, material } = props;

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', pt: isFirst ? 0 : 1 }}>
      {material != null && (
        <Box sx={{ display: 'inline-flex', mr: 1 }}>
          <MaterialThumb material={material} />
        </Box>
      )}
      {children}
    </Box>
  );
}

export default EditorDetailsCard;
